<script setup lang="ts">
import { computed, onMounted, ref, watch } from "vue";
import axios from "axios";
import { endpoint } from "@/remote/utils";
import _ from "lodash";
import {
  parseIso8601WithTZ,
  formatDateToLocalString,
  parseLocalStringToDate,
  formatDateToIsoWithTZ
} from "@/lib/dateUtils.ts";
import { useI18n } from "vue-i18n";

const { t } = useI18n({ inheritLocale: true, useScope: 'local' });

const useAlerts = defineModel('useAlerts');
const loadingOverlay = defineModel('loadingOverlay');
const { originalPark } = defineProps<{
  originalPark: {
    id: number,
    name: string,
  } | null,
}>();
const parkId = computed(() => originalPark?.id ?? null);
const emit = defineEmits(['updated']);


type RowState = 'dirty' | 'created' | 'deleted';
type RowStates = {
  [key in number]: RowState
}

const ctable = ref<Closure[]>([]);
const grid = ref();
const newId = ref(1);
const rowStates = ref<RowStates>({});

type RemoteClosure = {
  id: number,
  park: number,
  start: string,
  stop: string | null,
  note: string,
}

type Closure = {
  id: number,
  park: number,
  start: Date,
  stop: Date | null,
  note: string,
};

function intoClosure(rcls: RemoteClosure): Closure {
  return {
    ...rcls,
    start: parseIso8601WithTZ(rcls.start),
    stop: rcls.stop !== null ? parseIso8601WithTZ(rcls.stop) : null
  };
}

function intoRemoteClosure(cls: Closure): RemoteClosure {
  return {
    ...cls,
    start: formatDateToIsoWithTZ(cls.start),
    stop: cls.stop !== null ? formatDateToIsoWithTZ(cls.stop) : null
  };
}

async function fetchCTable(park_pk: number): Promise<Closure[]> {
  const url = `/internal/current/parks/${park_pk}/closures/`;
  try {
    const res = await axios.get<RemoteClosure[]>(endpoint(url), { withCredentials: true });
    return res.data.map(intoClosure);
  } catch (e: any) {
    if (e.response && e.response.status == 404) {
      return [];  // ?
    }
    return []; // ??? this was null
  }
}

async function deleteClosure(closureId: number) {
  const url = `/internal/current/parks/${parkId.value}/closures/${closureId}/`;
  return await axios.delete(endpoint(url), { withCredentials: true });
}

async function updateClosure(closure: Closure) {
  const url = `/internal/current/parks/${parkId.value}/closures/${closure.id}/`;
  return await axios.put(endpoint(url), intoRemoteClosure(closure), { withCredentials: true });
}

async function createClosure(closure: Closure) {
  const url = `/internal/current/parks/${parkId.value}/closures/`;
  const { id, ...payload } = intoRemoteClosure(closure);
  return await axios.post(endpoint(url), payload, { withCredentials: true });
}

async function newLine() {
  grid.value.newRecord({
    id: newId.value,
    start: new Date(),
    stop: null,
    note: '',
    park: parkId.value,
  });
  rowStates.value[newId.value] = 'created';
  newId.value += 1;
}

async function initialization() {
  if (originalPark !== null) {
    loadingOverlay.value = true;
    try {
      ctable.value = await fetchCTable(originalPark.id);
      if (ctable.value.length > 0) newId.value = Math.max(...ctable.value.map((c) => c.id)) + 1;
      rowStates.value = {};
    } finally {
      loadingOverlay.value = false;
    }
  }
}

function onCellUpdate(args: any) {
  const data = _(args)
    .flatMap(change => change.keys)
    .uniq()
    .value();
  for (const key of data) {
    if (rowStates.value[key] !== 'created') rowStates.value[key] = 'dirty';
  }
}

function onRowDelete(args: any) {
  const data = _(args).map(removed => removed.id).uniq().value();
  for (const key of data) {
    rowStates.value[key] = 'deleted';
  }
}

function ctableGetRow(id: number): Closure | null {
  return ctable.value.find((v) => v.id === id) ?? null;
}

function validate(): boolean {
  for (const closure of ctable.value) {
    if (closure.stop !== null && closure.stop < closure.start) {
      const msg = `Row with ID ${closure.id} end before starting!`;
      //@ts-ignore
      useAlerts.value.errorAlert(`Closure Table Validation Error`, msg);
      return false;
    }
  }
  return true;
}

async function submit() {
  if (!validate()) return;
  try {
    loadingOverlay.value = true;
    console.log("SUBMIT");
    // Initialize arrays to store keys for each state
    const dirtyKeys: number[] = [];
    const createdKeys: number[] = [];
    const deletedKeys: number[] = [];

    _.forOwn(rowStates.value, (state, key) => {
      const numKey = parseInt(key, 10); // Convert key to a number
      if (state === 'dirty') {
        dirtyKeys.push(numKey);
      } else if (state === 'created') {
        createdKeys.push(numKey);
      } else if (state === 'deleted') {
        deletedKeys.push(numKey);
      }
    });
    const updateMsg = `Update: ${dirtyKeys}, Create: ${createdKeys}, Delete: ${deletedKeys}`;
    console.log(updateMsg);
    const thingsToDo: Promise<any>[] = [];
    // DELETE
    for (const dkey of deletedKeys) {
      thingsToDo.push(deleteClosure(dkey));
    }
    // UPDATE
    for (const ukey of dirtyKeys) {
      const closure = ctableGetRow(ukey);
      if (closure !== null) thingsToDo.push(updateClosure(closure));
      else console.error(`Update on Key ${ukey} point to null Closure`);
    }
    // CREATE
    for (const ckey of createdKeys) {
      const closure = ctableGetRow(ckey);
      if (closure !== null) thingsToDo.push(createClosure(closure));
      else console.error(`Create on Key ${ckey} point to null Closure`);
    }
    // DO EVERYTHING
    await Promise.all(thingsToDo);
    // Reset States
    rowStates.value = {};
    // Notify
    //@ts-ignore
    useAlerts.value.successAlert(t('closure_table_submitted'), updateMsg);
  } catch (e: any) {
    //@ts-ignore
    useAlerts.value.errorAlert(t('closure_table_generic_error'), e.toString());
    console.error(e);
  } finally {
    loadingOverlay.value = false;
  }
}

function stopToText(value: Date | null): string {
  return value !== null ? formatDateToLocalString(value) : '';
}

function stopToValue(text: string): Date | null {
  return text !== '' ? parseLocalStringToDate(text) : null;
}

watch(() => originalPark, initialization);
onMounted(initialization);
</script>

<template>
  <v-card>
    <v-expansion-panels>
      <v-expansion-panel>
        <v-expansion-panel-title class="pa-0 pe-3">
          <v-card-title>
            {{ t('closure_table') }}
          </v-card-title>
        </v-expansion-panel-title>
        <v-expansion-panel-text>
          <v-row>
            <v-col>
              <vue-excel-editor ref="grid" v-model="ctable" height="400px" @update="onCellUpdate"
                                @delete="onRowDelete">
                <vue-excel-column
                  field="id"
                  :label="t('id')"
                  type="number"
                  key-field
                  readonly
                  width="40px"
                />
                <vue-excel-column
                  field="start"
                  :label="t('start_time')"
                  type="datetime"
                  :to-text="formatDateToLocalString"
                  :to-value="parseLocalStringToDate"
                  width="220px"
                />
                <vue-excel-column
                  field="stop"
                  :label="t('stop_time')"
                  type="datetime"
                  :to-text="stopToText"
                  :to-value="stopToValue"
                  width="220px"
                />
                <vue-excel-column
                  field="note"
                  :label="t('notes')"
                  type="string"
                  auto-fill-width
                />
              </vue-excel-editor>
            </v-col>
          </v-row>
          <v-row>
            <v-col class="d-flex flex-row justify-space-between">
              <div class="d-flex flex-row ga-2">
                <v-btn @click="newLine">{{ t('new_entry') }}</v-btn>
                <v-btn @click="grid.deleteSelectedRecords()">{{ t('delete') }}</v-btn>
              </div>
              <v-btn @click="submit">{{ t('submit') }}</v-btn>
            </v-col>
          </v-row>
        </v-expansion-panel-text>
      </v-expansion-panel>
    </v-expansion-panels>
  </v-card>
</template>

<i18n>
{
  "en": {
    "closure_table": "Closure Table",
    "id": "ID",
    "start_time": "Start Time",
    "stop_time": "Stop Time",
    "notes": "Notes",
    "new_entry": "New Entry",
    "delete": "Delete",
    "submit": "SUBMIT",
    "closure_table_submitted": "Closure Table submitted",
    "closure_table_generic_error": "Closure Table Generic Error"
  },
  "it": {
    "closure_table": "Tabella Chiusure",
    "id": "ID",
    "start_time": "Ora di Inizio",
    "stop_time": "Ora di Fine",
    "notes": "Note",
    "new_entry": "Nuova Voce",
    "delete": "Elimina",
    "submit": "INVIA",
    "closure_table_submitted": "Tabella Chiusure inviata",
    "closure_table_generic_error": "Errore Generico Tabella Chiusure"
  },
  "fr": {
    "closure_table": "Tableau de Fermeture",
    "id": "ID",
    "start_time": "Heure de Début",
    "stop_time": "Heure de Fin",
    "notes": "Notes",
    "new_entry": "Nouvelle Entrée",
    "delete": "Supprimer",
    "submit": "SOUMETTRE",
    "closure_table_submitted": "Tableau de Fermeture soumis",
    "closure_table_generic_error": "Erreur Générique du Tableau de Fermeture"
  }
}
</i18n>

<style scoped>

</style>
